import { useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { Spinner } from "@fluentui/react-components";
import { Search } from "lucide-react";

import ContractTable from "./contract-table";

import { useAdminContractsQuery } from "@/queries/admin/contracts";

const AdminContracts = () => {
  const [searchString, setSearchString] = useState("");

  const { adminContracts, adminContractsLoading } = useAdminContractsQuery();

  const contracts = useMemo(() => {
    if (!adminContracts) {
      return [];
    } else if (!searchString) {
      return adminContracts;
    } else {
      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
      const filterString = searchString.toLowerCase();
      return adminContracts.filter(
        (contract) =>
          contract.name.toLowerCase().includes(filterString) ||
          contract.jobOpening?.role?.toLowerCase().includes(filterString) ||
          contract.jobOpening?.company.name
            ?.toLowerCase()
            .includes(filterString),
      );
      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
    }
  }, [adminContracts, searchString]);

  const count = useMemo(() => contracts?.length, [contracts]);

  if (adminContractsLoading) {
    return (
      <div>
        <h3 className="py-4 text-2xl font-semibold group-hover:underline text-left">
          Contracts
        </h3>
        <div className="w-full flex-col justify-around align-middle">
          <Spinner size="huge" />
          <p className="text-center text-base font-medium text-[#727E94]">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="py-4 text-2xl font-semibold group-hover:underline text-left">
        Contracts
      </h3>
      <div className="w-full flex flex-col gap-4 items-start self-stretch">
        <div className="flex flex-col md:flex-row md:items-center justify-between self-stretch">
          <p className="text-lg text-[#181d27] font-semibold">
            {`${count} ${count === 1 ? "Contract" : "Contracts"}`}
          </p>
          <div className="flex flex-row items-center gap-3">
            <div
              className={cn(
                "flex flex-row items-center gap-0.5",
                "border-b border-[#57576B] transition-colors focus-within:border-black",
              )}
            >
              <Search />
              <input
                type="text"
                className={cn(
                  "flex h-10 w-full min-w-48 px-3 py-2 text-base",
                  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                )}
                placeholder="Search for any contract"
                value={searchString}
                onChange={(ev) => {
                  ev.preventDefault();
                  setSearchString(ev.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <ContractTable contracts={contracts} />
      </div>
    </div>
  );
};

export default AdminContracts;
